import _ from 'underscore';

import { uncheckedInjectionContext as globalContext } from '@bingads-webui-campaign/injection-context';
import { accountMetricCategoriesFactory } from '@bingads-webui/custom-columns-shared-config';
import { applyPartialConversionColumnName, getColumnsConversionMap } from '@bingads-webui-campaign/column-conversions';

import * as CommonUtils from '../common-utils';
import { getSharedColumnConfiguration } from '../columns/shared-column-configuration';
import { columnConfiguration } from '../columns/index';
import { gridsMap } from '../constants';

const i18n = globalContext.require('@bingads-webui/i18n-amd-loader!campaignui/feature/accounts-summary');
const i18nGlobal = globalContext.require('$/i18n!legacy/global');
const { Columns, SelectedColumns } = globalContext.require('component/bingads-grid/service/column-chooser-preferences');
const { MCCCustomColumns } = globalContext.require('component/bingads-grid/service/custom-column-preferences');
const { LegacyColumns, LegacySelectedColumns } = globalContext.require('component/bingads-grid/service/legacy-column-chooser-preferences');
const globalConfig = globalContext.require('component/config/index');
const logger = globalContext.require('module/service/instrumentation/index');
const { getLockedColumns, columnNames } = getSharedColumnConfiguration(globalConfig);

const {
  isInManagementOrBudgetTab,
  isInPerformanceTab,
  isInSmartPerformanceTab,
} = CommonUtils;

const permissions = globalConfig.get('Permissions');
const {
  IsCustomerHierarchyEnabled,
  IsCustomColumnsInAccountsSummarySupported,
  IsAvgPositionDeprecationPhase2Enabled,
  IsViewThroughConversionEnabled,
  IsSmartUIEnabled,
  IsAudienceSOVEnabled,
  IsAvgPositionDeprecationPhase1Enabled,
  IsAccountLabelsEnabled,
  dynamic = {},
  IsViewThroughConversionsRevenueEnabled,
} = permissions;

const {
  ColumnConversion: IsColumnConversionEnabled,
  PartialConversions: IsPartialConversionsEnabled,
  ConvValueRules: isConversionValueRuleColumnsEnabled,
  CBTLifetimeBudget: IsCbtLifetimeBudgetEnabled,
} = dynamic;
const usePartialConversions = IsColumnConversionEnabled && IsPartialConversionsEnabled;

export const columnChooserConfiguration = ({
  gridType,
  isForVNext,
  filterService,
  hasUnifiedSmartAccount = false,
}) => {
  const { gridName } = gridsMap[gridType];
  const isManagementGrid = isInManagementOrBudgetTab(gridType);
  const isPerformanceGrid = isInPerformanceTab(gridType);
  const isSmartPerformanceGrid = isInSmartPerformanceTab(gridType);
  const lockedColumns = getLockedColumns({ gridType, isForVNext });
  let customColumnConfig;

  if (IsCustomColumnsInAccountsSummarySupported && isPerformanceGrid && isForVNext) {
    const { metricCategories, segmentCategories } =
      accountMetricCategoriesFactory(permissions);

    customColumnConfig = {
      enabled: true,
      metricCategories,
      segmentCategories,
      filterService,
    };
  }

  const columnTitleMap = {};

  _.each(columnConfiguration({ gridType, isForVNext, hasUnifiedSmartAccount }), (columnInstance) => {
    columnTitleMap[columnInstance.name] = columnInstance.title;
  });

  const columnWithNewTagMap = {
    AccountVerificationStatus: true,
    BusinessLegalName: true,
  };

  const makeOptions = (category, selectOption = 'optional') => _.chain(category)
    .map((columnName) => {
      const columnTitle = columnTitleMap[columnName];
      const isNewColumn = columnWithNewTagMap[columnName];

      return {
        key: columnName,
        name: columnTitle,
        selectOption,
        isNewColumn,
      };
    })
    .value();

  const geti18nString = key => (isForVNext ? i18n.getString(key) : i18nGlobal.getString(key));
  let categories = [];
  if (isManagementGrid) {
    categories = [
      {
        name: geti18nString('Attributes'),
        isNewCategory: true,
        options: [
          ...makeOptions(lockedColumns, 'locked'),
          ...makeOptions(_.compact([
            (IsSmartUIEnabled || hasUnifiedSmartAccount) && 'AccountMode',
            IsCustomerHierarchyEnabled && 'AccountType',
            'Owner',
            'AccountVerificationStatus',
            IsCustomerHierarchyEnabled && 'ManagerAccounts',
            'BillToCustomerName',
            columnNames.PaymentSetting,
            columnNames.Balance,
            columnNames.Currency,
            columnNames.BillingLanguage,
            'BusinessLegalName',
            columnNames.BusinessLocation,
            columnNames.AccountTimeZone,
            columnNames.TaxId,
            columnNames.PaymentMethod,
            columnNames.DailyBudget,
            IsCbtLifetimeBudgetEnabled && columnNames.CampaignLifetimeBudget,
            columnNames.AutoTagging,
            columnNames.PrimaryContact,
            IsAccountLabelsEnabled && columnNames.Labels,
          ]))],
      },
    ];
  } else if (isSmartPerformanceGrid) {
    // Limit column chooser to UP Smart supported columns only
    categories = [
      {
        name: geti18nString('Attributes'),
        options: [
          ...makeOptions(lockedColumns, 'locked'),
          ...makeOptions(_.compact([
            'AccountMode',
            IsCustomerHierarchyEnabled && 'AccountType',
            'Owner',
            IsCustomerHierarchyEnabled && 'ManagerAccounts',
            columnNames.Currency,
            'BillToCustomerName',
            columnNames.DailyBudget,
            IsCbtLifetimeBudgetEnabled && columnNames.CampaignLifetimeBudget,
            IsAccountLabelsEnabled && columnNames.Labels,
          ])),
        ],
      },
      {
        name: geti18nString('Performance'),
        options: makeOptions(_.compact([
          'Clicks',
          'Impressions',
          'CTR',
          'TotalEffectiveCost',
        ])),
      },
      {
        name: geti18nString('Conversions'),
        options: makeOptions(_.compact([
          'CPA',
          'Conversions',
          'ConversionRate',
        ])),
      },
    ];
  } else {
    categories = [
      {
        name: geti18nString('Attributes'),
        options: [
          ...makeOptions(lockedColumns, 'locked'),
          ...makeOptions(_.compact([
            (IsSmartUIEnabled || hasUnifiedSmartAccount) && 'AccountMode',
            IsCustomerHierarchyEnabled && 'AccountType',
            'Owner',
            IsCustomerHierarchyEnabled && 'ManagerAccounts',
            columnNames.Currency,
            'BillToCustomerName',
            'IOPurchaseOrder',
            'IOBudget',
            'RemainingIOBudget',
            'StartDate',
            'EndDate',
            columnNames.DailyBudget,
            IsCbtLifetimeBudgetEnabled && columnNames.CampaignLifetimeBudget,
            'QualityScore',
            IsAccountLabelsEnabled && columnNames.Labels,
          ])),
        ],
      },
      {
        name: geti18nString('Performance'),
        options: makeOptions(_.compact([
          'Clicks',
          'Impressions',
          'CTR',
          'AverageCPC',
          'AverageCPM',
          'VideoViews',
          'ViewThroughRate',
          'AverageCPV',
          'TotalWatchTimeInMS',
          'AverageWatchTimePerImpression',
          'AverageWatchTimePerVideoView',
          'VideoViewsAt25Percent',
          'VideoViewsAt50Percent',
          'VideoViewsAt75Percent',
          'CompletedVideoViews',
          'VideoCompletionRate',
          !IsAvgPositionDeprecationPhase2Enabled && 'AveragePosition',
          'TotalEffectiveCost',
          'TopImpressionRate',
          'AbsoluteTopImpressionRate',
        ])),
      },
      {
        name: geti18nString('Conversions'),
        options: makeOptions(applyPartialConversionColumnName(permissions, _.compact([
          'CPA',
          'Conversions',
          'ConversionRate',
          'AdvertiserReportedRevenue',
          'RevenueOnAdSpend',
          'AllConversionCPA',
          'AllConversions',
          'AllConversionRate',
          'AllConversionRevenueOnAdSpend',
          'AllConversionAdvertiserReportedRevenue',
          isConversionValueRuleColumnsEnabled && 'AdvertiserReportedRevenueAdjustment',
          isConversionValueRuleColumnsEnabled && 'ViewThroughConversionsRevenueAdjustment',
          isConversionValueRuleColumnsEnabled && 'AllConversionAdvertiserReportedRevenueAdjustment',
          IsViewThroughConversionEnabled && 'ViewThroughConversions',
          IsViewThroughConversionsRevenueEnabled && 'ViewThroughConversionsRevenue',
          IsViewThroughConversionsRevenueEnabled && 'ViewThroughConversionsCPA',
          IsViewThroughConversionsRevenueEnabled && 'ViewThroughConversionsReturnOnAdSpend',
          IsViewThroughConversionsRevenueEnabled && 'ViewThroughConversionsRate',
          dynamic.NewCustomerAcquisition && 'NewCustomerConversionRate',
          dynamic.NewCustomerAcquisition && 'NewCustomerConversions',
          dynamic.NewCustomerAcquisition && 'NewCustomerCPA',
          dynamic.NewCustomerAcquisition && 'NewCustomerRevenue',
          dynamic.NewCustomerAcquisition && 'NewCustomerRevenueOnAdSpend',
          dynamic.CPSReporting && 'SalesCount',
          dynamic.InstallPerformanceColumns && 'Installs',
        ]))),
      },
      {
        name: geti18nString('Competitive'),
        options: makeOptions(_.compact([
          'ImpShare',
          'ExactMatchImpressionSharePercent',
          'ImpShareLostToBudget',
          'ImpShareLostToRank',
          'ClickSharePercent',
          'TopImpressionSharePercent',
          'TopImpressionShareLostToBudget',
          'TopImpressionShareLostToRank',
          'AbsoluteTopImpressionSharePercent',
          'AbsoluteTopImpressionShareLostToBudget',
          'AbsoluteTopImpressionShareLostToRank',
          IsAudienceSOVEnabled && 'AudienceImpressionShare',
          IsAudienceSOVEnabled && 'AudienceImpressionShareLostToBudget',
          IsAudienceSOVEnabled && 'AudienceImpressionShareLostToRank',
        ])),
      },
    ];
  }

  const columnChooserConfig = {
    i18n,
    Columns,
    SelectedColumns,
    LegacyColumns,
    LegacySelectedColumns,
    CustomColumns: MCCCustomColumns,
    customColumnConfig,
    categories,
    selected: lockedColumns.concat(_.compact([
      IsCustomerHierarchyEnabled && 'AccountType',
      (isPerformanceGrid || isSmartPerformanceGrid) && 'AccountMode',
      'Owner',
      isManagementGrid && 'AccountVerificationStatus',
      IsCustomerHierarchyEnabled && 'ManagerAccounts',
      (isPerformanceGrid || isSmartPerformanceGrid) && 'Clicks',
      (isPerformanceGrid || isSmartPerformanceGrid) && 'Impressions',
      (isPerformanceGrid || isSmartPerformanceGrid) && 'CTR',
      isPerformanceGrid && 'AverageCPC',
      (isPerformanceGrid || isSmartPerformanceGrid) && 'TotalEffectiveCost',
      isSmartPerformanceGrid && 'Conversions',
      isPerformanceGrid && usePartialConversions && 'ConversionsCredit',
      isPerformanceGrid && !usePartialConversions && 'Conversions',
      (isPerformanceGrid || isSmartPerformanceGrid) && 'TopImpressionRate',
      (isPerformanceGrid || isSmartPerformanceGrid) && 'AbsoluteTopImpressionRate',
      isManagementGrid && 'BillToCustomerName',
      isManagementGrid && columnNames.PaymentSetting,
      isManagementGrid && columnNames.Balance,
      isManagementGrid && columnNames.Currency,
      isManagementGrid && 'BusinessLegalName',
      isManagementGrid && columnNames.BusinessLocation,
      isManagementGrid && columnNames.TaxId,
      isManagementGrid && columnNames.PaymentMethod,
      isManagementGrid && columnNames.DailyBudget,
      isManagementGrid && IsCbtLifetimeBudgetEnabled && columnNames.CampaignLifetimeBudget,
      isManagementGrid && columnNames.PrimaryContact,
    ])),

    locked: lockedColumns,
    patchQuery: true,

    ...isPerformanceGrid ? {
      columnConversions: getColumnsConversionMap(gridName, permissions),
    } : {},
  };

  if (isForVNext) {
    columnChooserConfig.customToolbarIcon = 'ba-TripleColumnSolid';
  }

  // Logging default cols to sort out warmup cache issue, remove after problem fixed
  logger.logMessage([logger.constants.LOGCATEGORY.TRACE], logger.constants.LOGLEVEL.INFO, '/accounts/performance', `Default columns used in warmup query in column-chooser-config.js: ${columnChooserConfig.selected}, SelectedColumns: ${SelectedColumns}, LegacySelectedColumns: ${LegacySelectedColumns}, isPerformanceGrid: ${isPerformanceGrid}, IsAvgPositionDeprecationPhase1Enabled: ${IsAvgPositionDeprecationPhase1Enabled}`);

  return columnChooserConfig;
};
